import { jwtDecode } from 'jwt-decode';
import { loadSettings } from '../settings/settingsStorage';

const AUTHORIZE_URL = 'https://login.xero.com/identity/connect/authorize';
const TOKEN_URL = 'https://identity.xero.com/connect/token';
const CONNECTIONS_URL = 'https://api.xero.com/connections';
const SCOPES =
  'openid profile email accounting.contacts.read accounting.invoices.read offline_access';

const STATE_KEY = 'xero_oauth_state';

/** Strip trailing slashes so authorize + token exchange match Xero’s registered URI. */
function normalizeRedirectUri(uri: string): string {
  return uri.replace(/\/+$/, '');
}

/**
 * Must exactly match a redirect URI configured for this app in the Xero Developer Portal.
 * Priority: `draftRedirectUri` (if non-empty) → saved API setting → REACT_APP_XERO_REDIRECT_URI → origin + /auth/callback
 */
function resolveXeroRedirectUri(draftRedirectUri?: string | null): string {
  const fromDraft = draftRedirectUri?.trim();
  if (fromDraft) {
    return normalizeRedirectUri(fromDraft);
  }
  const saved = loadSettings().integrations.xero.redirectUri?.trim();
  if (saved) {
    return normalizeRedirectUri(saved);
  }
  const fromEnv = process.env.REACT_APP_XERO_REDIRECT_URI?.trim();
  if (fromEnv) {
    return normalizeRedirectUri(fromEnv);
  }
  return `${normalizeRedirectUri(window.location.origin)}/auth/callback`;
}

export function getXeroRedirectUri(): string {
  return resolveXeroRedirectUri();
}

/** Same logic as OAuth, but lets API Settings preview the URI before Save using the draft field. */
export function previewXeroRedirectUri(draftRedirectUri: string): string {
  return resolveXeroRedirectUri(draftRedirectUri);
}

interface TokenSet {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  id_token: string;
}

function clientId(): string {
  const fromSettings = loadSettings().integrations.xero.clientId?.trim();
  const id = fromSettings || process.env.REACT_APP_XERO_CLIENT_ID?.trim();
  if (!id) {
    throw new Error('Add Xero Client ID in API Settings (or set REACT_APP_XERO_CLIENT_ID)');
  }
  return id;
}

function clientSecret(): string {
  const fromSettings = loadSettings().integrations.xero.clientSecret?.trim();
  const s = fromSettings || process.env.REACT_APP_XERO_CLIENT_SECRET?.trim();
  if (!s) {
    throw new Error('Add Xero Client Secret in API Settings (or set REACT_APP_XERO_CLIENT_SECRET)');
  }
  return s;
}

function toStoredTokens(raw: Record<string, unknown>): TokenSet {
  const access_token = raw.access_token as string | undefined;
  const refresh_token = raw.refresh_token as string | undefined;
  const expires_in = raw.expires_in as number | undefined;
  if (!access_token || !refresh_token) {
    throw new Error('Incomplete token response from Xero');
  }
  return {
    access_token,
    refresh_token,
    expires_in: expires_in ?? 1800,
    id_token: (raw.id_token as string) ?? '',
  };
}

class XeroAuthService {
  private static instance: XeroAuthService;
  private tokenSet: TokenSet | null = null;
  private tenantId: string | null = null;

  private constructor() {
    this.loadTokens();
  }

  static getInstance(): XeroAuthService {
    if (!XeroAuthService.instance) {
      XeroAuthService.instance = new XeroAuthService();
    }
    return XeroAuthService.instance;
  }

  public getRedirectUri(): string {
    return getXeroRedirectUri();
  }

  public getAuthUrl(): string {
    const state =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(STATE_KEY, state);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId(),
      redirect_uri: resolveXeroRedirectUri(),
      scope: SCOPES,
      state,
    });
    return `${AUTHORIZE_URL}?${params.toString()}`;
  }

  public async handleCallback(queryString: string): Promise<void> {
    const normalized = queryString.startsWith('?') ? queryString.slice(1) : queryString;
    const params = new URLSearchParams(normalized);
    const err = params.get('error');
    if (err) {
      throw new Error(params.get('error_description') || err);
    }
    const code = params.get('code');
    const state = params.get('state');
    const expected = sessionStorage.getItem(STATE_KEY);
    sessionStorage.removeItem(STATE_KEY);
    if (!code) {
      throw new Error('No authorization code returned');
    }
    if (!state || !expected || state !== expected) {
      throw new Error('Invalid OAuth state');
    }

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId(),
      client_secret: clientSecret(),
      code,
      redirect_uri: resolveXeroRedirectUri(),
    });

    const res = await fetch(TOKEN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    const data = (await res.json()) as Record<string, unknown>;
    if (!res.ok) {
      const msg =
        (data.error_description as string) || (data.error as string) || res.statusText;
      throw new Error(msg);
    }

    this.tokenSet = toStoredTokens(data);
    await this.setTenantId();
    this.saveTokens();
  }

  private async setTenantId(): Promise<void> {
    const token = this.tokenSet?.access_token;
    if (!token) {
      throw new Error('No access token');
    }
    const res = await fetch(CONNECTIONS_URL, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });
    const data = (await res.json()) as { Detail?: string; Message?: string };
    if (!res.ok) {
      throw new Error(data.Detail || data.Message || 'Failed to load Xero organisations');
    }
    const tenants = data as Array<{ tenantId?: string }>;
    if (!tenants?.length || !tenants[0].tenantId) {
      throw new Error('No tenants found');
    }
    this.tenantId = tenants[0].tenantId;
  }

  public async refreshToken(): Promise<void> {
    if (!this.tokenSet?.refresh_token) {
      throw new Error('No refresh token available');
    }
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: clientId(),
      client_secret: clientSecret(),
      refresh_token: this.tokenSet.refresh_token,
    });
    const res = await fetch(TOKEN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    const data = (await res.json()) as Record<string, unknown>;
    if (!res.ok) {
      const msg =
        (data.error_description as string) || (data.error as string) || res.statusText;
      throw new Error(msg);
    }
    const merged = {
      ...data,
      refresh_token:
        (data.refresh_token as string) || this.tokenSet?.refresh_token || '',
    };
    this.tokenSet = toStoredTokens(merged);
    this.saveTokens();
  }

  public isAuthenticated(): boolean {
    if (!this.tokenSet?.access_token) {
      return false;
    }
    try {
      const decoded = jwtDecode<{ exp?: number }>(this.tokenSet.access_token);
      if (!decoded.exp) {
        return true;
      }
      return Date.now() < decoded.exp * 1000;
    } catch {
      return false;
    }
  }

  public async ensureValidAccessToken(): Promise<void> {
    this.loadTokens();
    if (this.isAuthenticated()) {
      return;
    }
    if (this.tokenSet?.refresh_token) {
      await this.refreshToken();
    }
  }

  public getAccessToken(): string | null {
    if (!this.isAuthenticated()) {
      return null;
    }
    return this.tokenSet?.access_token ?? null;
  }

  public getTenantId(): string | null {
    return this.tenantId;
  }

  private saveTokens(): void {
    if (this.tokenSet) {
      localStorage.setItem('xero_tokens', JSON.stringify(this.tokenSet));
      localStorage.setItem('xero_tenant_id', this.tenantId || '');
    }
  }

  public loadTokens(): void {
    const savedTokens = localStorage.getItem('xero_tokens');
    const savedTenantId = localStorage.getItem('xero_tenant_id');
    if (savedTokens) {
      try {
        this.tokenSet = JSON.parse(savedTokens) as TokenSet;
      } catch {
        this.tokenSet = null;
      }
    }
    if (savedTenantId) {
      this.tenantId = savedTenantId;
    }
  }

  public hasStoredSession(): boolean {
    this.loadTokens();
    return Boolean(this.tokenSet?.refresh_token);
  }

  public logout(): void {
    this.tokenSet = null;
    this.tenantId = null;
    localStorage.removeItem('xero_tokens');
    localStorage.removeItem('xero_tenant_id');
  }
}

export default XeroAuthService.getInstance();
